import React, { useCallback, useState, useEffect } from "react";
import { teacherUploadAikenExam, teacherGetExamSettings, teacherUpdateExamSettings } from "../../../api/academic";

export default function TeacherExamTab({ runId }) {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(null);

    const [settings, setSettings] = useState({ time_limit_minutes: "", passing_score: 60, max_attempts: 1, is_locked: false, attempts_count: 0 });
    const [savingSettings, setSavingSettings] = useState(false);
    const [settingsError, setSettingsError] = useState(null);
    const [settingsSuccess, setSettingsSuccess] = useState(null);

    const fetchSettings = useCallback(async () => {
        try {
            const settingsData = await teacherGetExamSettings(runId).catch(() => null);
            if (settingsData) {
                setSettings({
                    time_limit_minutes: settingsData.time_limit_minutes || "",
                    passing_score: settingsData.passing_score || 60,
                    max_attempts: settingsData.max_attempts || 1,
                    is_locked: Boolean(settingsData.is_locked),
                    attempts_count: settingsData.attempts_count || 0
                });
            }
        } catch (e) {
            console.error(e);
        }
    }, [runId]);

    const isExamLocked = Boolean(settings.is_locked);

    useEffect(() => {
        if (runId) {
            fetchSettings();
        }
    }, [runId, fetchSettings]);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setFile(e.target.files[0]);
            setUploadError(null);
            setUploadSuccess(null);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            setUploadError("Selecciona un archivo .txt en formato Aiken.");
            return;
        }
        setUploading(true);
        setUploadError(null);
        setUploadSuccess(null);
        try {
            const res = await teacherUploadAikenExam(runId, file);
            setUploadSuccess(res.message || "Examen subido exitosamente.");
            setFile(null);
            const fileInput = document.getElementById("aiken-upload-input");
            if (fileInput) fileInput.value = "";
            fetchSettings();
        } catch (e) {
            setUploadError(e?.response?.data?.error || "Error al subir el examen.");
        } finally {
            setUploading(false);
        }
    };

    const handleSaveSettings = async () => {
        setSavingSettings(true);
        setSettingsError(null);
        setSettingsSuccess(null);
        try {
            const res = await teacherUpdateExamSettings(runId, settings);
            setSettingsSuccess(res.message || "Configuración guardada.");
            fetchSettings();
        } catch (e) {
            setSettingsError(e?.response?.data?.error || "Error al guardar configuración.");
        } finally {
            setSavingSettings(false);
        }
    };

    const handleDownloadTemplate = () => {
        const templateContent = `Pregunta 1 de ejemplo\nA. Primera opción\nB. Segunda opción\nC. Tercera opción\nANSWER: B\n\nPregunta 2 de ejemplo\nA) Opcion A\nB) Opcion B\nC) Opcion C\nANSWER: A`;
        const blob = new Blob([templateContent], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "formato_aiken_ejemplo.txt";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="teacher-exam-tab mt-4 font-n">
            <div className="aula-unit ">
                <div className="aula-unit-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                        <h3 className="aula-unit-title">
                            Gestión de Examen Final
                        </h3>
                    </div>
                </div>

                <div className="aula-unit-body p-4 p-md-5">
                    <div className="row mb-5">
                        <div className="col-md-12 col-lg-6">
                            <div>
                                <div className="card-body p-4">
                                    <div className="portal-emp-card-header">
                                        <span className="btn-admin">1</span>
                                        <div className="d-flex gap-2">
                                            <h5 className="mb-0">Cargar Preguntas</h5>
                                            <span className="text-muted fs-6">(Formato Aiken)</span>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="mb-2">
                                            Sube un archivo <code>.txt</code> con tus preguntas. El formato debe ser:
                                        </p>
                                        {isExamLocked && (
                                            <div className="alert alert-warning py-2">
                                                Este examen ya tiene intentos registrados. La carga Aiken queda bloqueada y cualquier reinicio debe hacerlo administración Somnus.
                                            </div>
                                        )}
                                        <div className="p-3 rounded border mb-3">
                                            <span className="text-muted small">¿Cuál es la capital de Chile?</span><br />
                                            <span className="text-muted small">A. Buenos Aires</span><br />
                                            <span className="text-muted small">B. Santiago</span><br />
                                            <span className="text-muted small">C. Lima</span><br />
                                            <span className="text-muted small">ANSWER: B</span>
                                        </div>
                                    </div>
                                    <div className="d-flex mb-4">
                                        <button
                                            type="button"
                                            className="adm-upd-btn px-3 py-1 "
                                            onClick={handleDownloadTemplate}
                                        >
                                            <i className="ri-download-line me-2"></i>
                                            <span>Descargar ejemplo</span>
                                        </button>
                                    </div>

                                    <input
                                        type="file"
                                        accept=".txt"
                                        id="aiken-upload-input"
                                        className="form-control mb-3"
                                        onChange={handleFileChange}
                                        disabled={uploading || isExamLocked}
                                    />
                                    <div className="d-flex">
                                        <button
                                            type="button"
                                            className="adm-upd-btn py-2"
                                            onClick={handleUpload}
                                            disabled={uploading || !file || isExamLocked}
                                        >
                                            <i className="ri-upload-cloud-2-line me-2"></i>
                                            <span>{uploading ? "Subiendo y Procesando..." : "Subir archivo Aiken"}</span>
                                        </button>
                                    </div>


                                    {uploadError && <div className="text-danger mt-3 small"><i className="ri-error-warning-line me-1"></i><span>{uploadError}</span></div>}
                                    {uploadSuccess && <div className="text-success mt-3 small"><i className="ri-checkbox-circle-line me-1"></i><span>{uploadSuccess}</span></div>}
                                </div>
                            </div>
                        </div>

                        <div className="col-md-12 col-lg-6">
                            <div className="card h-100 border-0">
                                <div className="card-body p-4">
                                    <div className="portal-emp-card-header ">
                                        <span className="btn-admin">2</span>
                                        <h5 className="mb-0">Configuración del Examen</h5>
                                    </div>

                                    <div className="mb-3">
                                        <div className="d-flex align-items-center mb-1 gap-2">
                                            <label className="form-label mb-0"><span>Tiempo Límite (minutos)</span></label>
                                            <span className="text-muted fs-6">(Dejar en blanco si no hay límite)</span>
                                        </div>

                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.time_limit_minutes}
                                            onChange={(e) => setSettings({ ...settings, time_limit_minutes: e.target.value })}
                                            placeholder="Ej: 60"
                                            disabled={isExamLocked}
                                        />
                                        <div className="form-text"><span>Tiempo que tendrá el alumno para completar el examen una vez iniciado.</span></div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="d-flex align-items-center mb-1 gap-2">
                                            <label className="form-label mb-0"><span>Límite de Intentos</span></label>
                                        </div>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={settings.max_attempts}
                                            onChange={(e) => setSettings({ ...settings, max_attempts: e.target.value })}
                                            placeholder="Ej: 1"
                                            min="1"
                                            disabled={isExamLocked}
                                        />
                                        <div className="form-text"><span>Cantidad máxima de veces que el alumno puede rendir el examen.</span></div>
                                        {isExamLocked && (
                                            <div className="alert alert-warning py-2 mt-3 mb-0">
                                                La configuración queda bloqueada porque hay {settings.attempts_count} intento(s) registrados. Solicita el ajuste a administración Somnus.
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        type="button"
                                        className="adm-save-btn py-2 mt-2"
                                        onClick={handleSaveSettings}
                                        disabled={savingSettings || isExamLocked}
                                    >
                                        <span>{savingSettings ? "Guardando..." : "Guardar Configuración"}</span>
                                    </button>

                                    {settingsError && <div className="text-danger mt-3 small"><i className="ri-error-warning-line me-1"></i><span>{settingsError}</span></div>}
                                    {settingsSuccess && <div className="text-success mt-3 small"><i className="ri-checkbox-circle-line me-1"></i><span>{settingsSuccess}</span></div>}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
